<template>
  <v-container grid-list-xs class="pa-0">
    <v-layout row wrap>
      <v-card class="elevation-4">
        <v-card-title class="info white--text">
          <h2 class="headline">Volume Calculator</h2>
        </v-card-title>
        <v-layout row wrap>
          <v-flex xs12 md5 class="border-right text-xs-center">
            <h3 class="display-1 mt-5 blue--text">Volume</h3>
            <h4 class="headline mt-3 pl-3 pr-3">
              {{ volume }}
              {{ unitType === "Metric" ? "litres" : "gallons" }}
            </h4>
            <v-btn v-if="!user.email" @click="sendToTest">Use in Test</v-btn>
            <v-btn
              v-else-if="settings.pools.length === 0"
              @click="showNoPoolMessage"
              >Apply to Pool</v-btn
            >
            <v-btn
              v-else-if="settings.pools.length === 1"
              @click="applyToPool(settings.pools[0].id)"
              >Apply to Pool</v-btn
            >
            <v-menu v-else offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-bind="attrs" v-on="on">Apply to Pool</v-btn>
              </template>
              <v-list>
                <v-list-tile
                  v-for="pool in settings.pools"
                  :key="pool.id"
                  @click="applyToPool(pool.id)"
                >
                  <v-list-tile-title>{{ pool.name }}</v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
            <p>
              Volume currently used in test:
              {{ settings.pool.volume || settings.volume }}
              {{
                (settings.pool.units || settings.unitType) === "Metric"
                  ? "litres"
                  : "gallons"
              }}
            </p>
          </v-flex>
          <v-flex xs12 md5 offset-md1 class="pt-3 pb-3">
            <v-form class="mt-2 mb-2 pa-2">
              <v-layout row wrap>
                <v-flex class="pl-1 xs12 md6">
                  <v-select
                    label="Units"
                    v-model="unitType"
                    :items="settings.unitTypes"
                  />
                </v-flex>
                <v-flex class="pl-1 xs12 md6">
                  <v-select
                    label="Shape"
                    v-model="shape"
                    :items="settings.shapes"
                    item-text="name"
                    :return-object="true"
                  />
                </v-flex>
                <v-flex class="pl-1 xs12 md4">
                  <v-text-field :label="labels.width" v-model="width" />
                </v-flex>
                <v-flex class="pl-1 xs12 md4" v-if="shape.name !== 'Round'">
                  <v-text-field :label="labels.length" v-model="length" />
                </v-flex>
                <v-flex class="pl-1 xs12 md4">
                  <v-text-field :label="labels.depth" v-model="depth" />
                </v-flex>
              </v-layout>
            </v-form>
            <v-snackbar v-model="snackbar.show" :bottom="true" :right="true">
              {{ snackbar.message }}
              <v-btn color="red" flat @click="snackbar = false">Close</v-btn>
            </v-snackbar>
          </v-flex>
        </v-layout>
      </v-card>
    </v-layout>
    <AdView id="165" />
    <PCModal />
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import AdView from "@/components/AdView";
import PCModal from "@/components/PCModal";
import firebase from "@/firebase";
import { initialValues as initialSettings } from "@/store/settings";

export default {
  components: { AdView, PCModal },
  data: function () {
    return {
      unitType: initialSettings.unitType,
      shape: initialSettings.shape,
      width: initialSettings.width,
      length: initialSettings.length,
      depth: initialSettings.depth,
      snackbar: {
        show: false,
        message: "",
      },
    };
  },
  computed: {
    ...mapGetters({
      fc: "fc/getFC",
      settings: "settings/getSettings",
      user: "user/getUser",
    }),
    labels: function () {
      return {
        length: `Length (${this.unitType === "Metric" ? "m" : "ft"})`,
        width: `Width (${this.unitType === "Metric" ? "m" : "ft"})`,
        depth: `Average Depth (${this.unitType === "Metric" ? "m" : "ft"})`,
      };
    },
    volume: function () {
      // this value is *sometimes* the area
      let value1 = this.length * this.width;
      if (this.shape.name == "Round") {
        value1 = this.width * this.width;
      } else if (this.shape.name == "Oval") {
        value1 -=
          0.214602 *
          (this.length < this.width
            ? this.length * this.length
            : this.width * this.width);
      }
      const volume = value1 * this.depth * this.shape.multiplier;
      const gallons = {
        "U.S.":
          volume < 1000
            ? Math.floor(volume / 10 + 0.5) * 10
            : Math.floor(volume / 100 + 0.5) * 100,
        Metric: Math.floor((volume / 7.48052) * 10 + 0.5) * 100,
        Imperial:
          volume * 0.832674 < 1000
            ? Math.floor((volume * 0.832674) / 10 + 0.5) * 10
            : Math.floor((volume * 0.832674) / 100 + 0.5) * 100,
      };
      return gallons[this.unitType];
    },
    poolsRef() {
      return firebase.db
        .collection("users")
        .doc(firebase.auth.currentUser.uid)
        .collection("pools");
    },
  },
  methods: {
    ...mapActions({
      initSettings: "settings/initSettings",
      saveSettings: "settings/saveSettings",
    }),
    ...mapMutations({
      calcVolume: "settings/calcVolume",
      setFC: "fc/setFC",
      setSettings: "settings/setSettings",
      updatePool: "settings/updatePool",
      updateSettingsPool: "settings/updateSettingsPool",
    }),
    sendToTest: function () {
      this.setSettings({
        unitType: this.unitType,
        volume: this.volume,
        shape: this.shape,
        length: this.length,
        width: this.width,
        depth: this.depth,
      });

      this.$router.push({ name: "calculator.test" });
    },
    showNoPoolMessage: function () {
      this.snackbar.message = "No Pool has been set up";
      this.snackbar.show = true;
    },
    applyToPool: async function (poolID) {
      const changes = {
        units: this.unitType,
        volume: this.volume,
      };
      await this.poolsRef.doc(poolID).update(changes);

      this.updatePool({ id: poolID, changes });

      if (this.settings.pool.id === poolID) {
        this.updateSettingsPool(changes);
      }

      this.snackbar.message = "Updated pool with new volume";
      this.snackbar.show = true;
    },
  },
  async mounted() {
    await this.initSettings();
    this.unitType = this.settings.unitType;
    console.log(this.settings);
  },
};
</script>

<style lang="scss" scoped>
.border-right {
  border-right: 1px solid #d8d8d8;
}
</style>
